import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { X, Plus, Check, Trash2, Package2, Info, AlertTriangle, Printer, Loader2, Pencil, ClipboardList, ArrowLeftRight, Split } from 'lucide-react';
import { PalletSummaryDto } from '../../../../types/picking';
import { printingService, LabelType } from '../../../../services/printingService';
import { SSCCLabelType } from '../../../../types/printing';
import { useAuth } from '../../../../contexts/AuthContext';
import { ScanTextInput } from '../../../common/inputs';
import { EnrichSSCCModal } from './modals/EnrichSSCCModal';
import { getCarrierDisplayName } from './palletHelpers';

interface PalletManagerProps {
  pallets: PalletSummaryDto[];
  activePalletSSCC: string | null;
  onCreatePallet: (ssccCode?: string) => void;
  onSetActivePallet: (sscc: string) => void;
  onDeletePallet: (sscc: string) => void;
  onClose: () => void;
  /** PList-ID for pallene — sendes ved SSCC-utskrift for OrderSSCC-oppslag i custom-etikett. */
  pListId?: number;
  /** Last pallene på nytt (etter berikelse) slik at oppdaterte verdier vises. */
  onReload?: () => void;
  /** Aktive SSCC-/pall-etikett-rapporter (ReportType 50) for rapportvelger. */
  ssccReports?: LabelType[];
  /** Valgt SSCC-rapport-ID som sendes ved utskrift. */
  selectedSsccReportId?: number;
  onSelectSsccReport?: (reportId: number | undefined) => void;
  /** Lastbærer-valg for nye paller (36334224). Tom liste skjuler velgeren. */
  carriers?: { transitPackagingID: number; transitPackagingDescr: string }[];
  selectedCarrierId?: number | null;
  onSelectCarrier?: (carrierId: number | null) => void;
  /** Bytt lastbærertype på en eksisterende pall (TW 36396318). Udefinert skjuler knappen. */
  onChangeCarrier?: (sscc: string, carrierId: number | null) => void;
  /** Plukket antall som ikke ligger på noen pall (TW 36396316). Vises i fordel-knappen. */
  unplacedQty?: number;
  /** Minst én linje er plukket (TW 36397706). Styrer om fordel-knappen vises — også uten lastbærer
   *  og også når alt allerede er plassert; modalen kan selv opprette lastbæreren. */
  hasPickedLines?: boolean;
  onDistribute?: () => void;
}

const FIRST_TIME_INFO_KEY = 'palletManager_firstTimeInfoDismissed';

export const PalletManager: React.FC<PalletManagerProps> = ({
  pallets,
  activePalletSSCC,
  onCreatePallet,
  onSetActivePallet,
  onDeletePallet,
  onClose,
  pListId,
  onReload,
  ssccReports = [],
  selectedSsccReportId,
  onSelectSsccReport,
  carriers = [],
  selectedCarrierId = null,
  onSelectCarrier,
  onChangeCarrier,
  unplacedQty = 0,
  hasPickedLines = false,
  onDistribute
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [newPalletSSCC, setNewPalletSSCC] = useState('');
  const [showFirstTimeInfo, setShowFirstTimeInfo] = useState(false);
  const [printingSSCC, setPrintingSSCC] = useState<string | null>(null);
  const [enrichSscc, setEnrichSscc] = useState<string | null>(null);
  const [printError, setPrintError] = useState<string | null>(null);
  const [printSuccess, setPrintSuccess] = useState<string | null>(null);
  // Typevelger åpen for denne pallen (TW 36396318)
  const [carrierPickerSscc, setCarrierPickerSscc] = useState<string | null>(null);

  const carrierFallbackName = t('warehouse.modules.picking.pallets.carrier', {}, 'Lastbærer');
  const carrierName = (sscc: string) => getCarrierDisplayName(pallets, sscc, carrierFallbackName);

  // Handle SSCC label printing. withContent=true → innholdsetikett (36334106):
  // etiketten inkluderer pallens varelinjer (inntil 12 + «+N flere»).
  const handlePrintSSCC = async (pallet: PalletSummaryDto, withContent: boolean = false) => {
    if (!user?.companyNo) {
      setPrintError(t('warehouse.modules.picking.errors.missingCompanyInfo'));
      return;
    }

    setPrintingSSCC(pallet.sscc);
    setPrintError(null);
    setPrintSuccess(null);

    try {
      const result = await printingService.printSSCCLabel(user.companyNo, {
        sscc: pallet.sscc,
        pListId,
        // Bruk valgt SSCC-rapport (ReportType 50) hvis tilgjengelig; ellers lar vi
        // backend falle tilbake til standard SSCC-etikett.
        // Innholdsetiketten (TW 36391636) løses i backend fra picking.contentLabelReportId,
        // deretter type-50-standarden — nedtrekket her gjelder kun vanlig SSCC-etikett.
        reportId: withContent ? undefined : selectedSsccReportId,
        quantity: pallet.totalQuantity || undefined,
        labelType: SSCCLabelType.StandardPallet,
        includeContent: withContent || undefined,
        // Get first item's info if available
        gtin: pallet.items?.[0]?.gtin || undefined,
        batchNo: pallet.items?.[0]?.itemBatchNo || undefined,
        expiryDate: pallet.items?.[0]?.itemBatchExpDate || undefined
      });

      if (result.success) {
        setPrintSuccess(t('warehouse.modules.picking.palletManager.printSuccess', { 0: pallet.sscc }));
        // Clear success message after 3 seconds
        setTimeout(() => setPrintSuccess(null), 3000);
        // Advarsel fra backend (TW 36391636): etiketten er skrevet, men f.eks. uten varelinjer.
        if (result.warning) {
          setPrintError(result.warning);
        }
      } else {
        setPrintError(result.message || t('warehouse.modules.picking.palletManager.printError'));
      }
    } catch (err: any) {
      console.error('Failed to print SSCC label:', err);
      setPrintError(err.message || t('warehouse.modules.picking.palletManager.printErrorLabel'));
    } finally {
      setPrintingSSCC(null);
    }
  };

  // Check if first-time info should be shown
  useEffect(() => {
    const dismissed = localStorage.getItem(FIRST_TIME_INFO_KEY);
    if (!dismissed && pallets.length === 0) {
      setShowFirstTimeInfo(true);
    }
  }, [pallets.length]);

  const handleCreatePallet = () => {
    onCreatePallet(newPalletSSCC);
    setNewPalletSSCC('');
  };

  const handleDismissFirstTimeInfo = () => {
    localStorage.setItem(FIRST_TIME_INFO_KEY, 'true');
    setShowFirstTimeInfo(false);
  };

  const getPalletStatusBadge = (pallet: PalletSummaryDto, isActive: boolean) => {
    const isEmpty = pallet.totalItems === 0;
    return (
      <div className="flex items-center gap-2">
        {isActive && (
          <span
            className="px-2 py-1 rounded text-xs font-bold text-white bg-[#F58220]"
            role="status"
            aria-label={t('warehouse.modules.picking.palletManager.activePalletAria')}
          >
            {t('warehouse.modules.picking.pallets.active')}
          </span>
        )}
        <span
          className={`px-2 py-1 rounded text-xs font-semibold ${
            isEmpty
              ? 'bg-gray-200 text-gray-700'
              : 'bg-[#E0F2F4] text-[#0E7C86]'
          }`}
          role="status"
          aria-label={isEmpty ? t('warehouse.modules.picking.palletManager.emptyPalletAria') : t('warehouse.modules.picking.palletManager.storedPalletAria')}
        >
          {isEmpty ? t('warehouse.modules.picking.pallets.empty') : t('warehouse.modules.picking.pallets.stored')}
        </span>
      </div>
    );
  };

  const handleDeletePallet = (pallet: PalletSummaryDto) => {
    const isEmpty = pallet.totalItems === 0;

    if (isEmpty) {
      // Delete empty pallet directly
      onDeletePallet(pallet.sscc);
    } else {
      // Require confirmation for filled pallets
      const confirmed = window.confirm(
        t('warehouse.modules.picking.pallets.confirmDelete', { 0: pallet.sscc, 1: pallet.totalItems, 2: pallet.totalQuantity })
      );

      if (confirmed) {
        onDeletePallet(pallet.sscc);
      }
    }
  };

  const handleDeleteAllEmpty = () => {
    const confirmed = window.confirm(
      t('warehouse.modules.picking.pallets.confirmDeleteAllEmpty', { 0: emptyPallets.length })
    );

    if (confirmed) {
      emptyPallets.forEach(pallet => onDeletePallet(pallet.sscc));
    }
  };

  // Separate empty and filled pallets
  const emptyPallets = pallets.filter(p => p.totalItems === 0);
  const filledPallets = pallets.filter(p => p.totalItems > 0);

  return (
    <div className="fixed inset-0 sm:right-0 sm:left-auto sm:top-0 h-full w-full sm:w-96 bg-white shadow-xl sm:border-l border-gray-200 z-50 overflow-y-auto">
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h3 className="text-lg font-semibold">{t('warehouse.modules.picking.pallets.management')}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label={t('warehouse.modules.picking.palletManager.closeManagerAria')}
          >
            <X size={24} />
          </button>
        </div>

        {/* Print success/error messages */}
        {printSuccess && (
          <div
            className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-green-800 flex items-center gap-2"
            role="status"
          >
            <Check size={18} className="flex-shrink-0" />
            <p className="text-sm">{printSuccess}</p>
          </div>
        )}

        {printError && (
          <div
            className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 flex items-center gap-2"
            role="alert"
          >
            <AlertTriangle size={18} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm">{printError}</p>
              <button
                onClick={() => setPrintError(null)}
                className="text-xs underline mt-1"
              >
                {t('common.buttons.close')}
              </button>
            </div>
          </div>
        )}

        {/* First-time info box */}
        {showFirstTimeInfo && (
          <div
            className="mb-4 p-4 rounded-lg border-2 border-[#0E7C86] bg-[#E0F2F4]"
            role="alert"
            aria-live="polite"
          >
            <div className="flex items-start gap-3 mb-3">
              <Info size={20} className="text-[#0E7C86] flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-[#0E7C86] mb-2">{t('warehouse.modules.picking.pallets.howPalletsWork')}</h4>
                <ul className="text-sm text-gray-700 space-y-1.5">
                  <li>• {t('warehouse.modules.picking.pallets.palletInfo1')}</li>
                  <li>• {t('warehouse.modules.picking.pallets.palletInfo2')}</li>
                  <li>• {t('warehouse.modules.picking.pallets.palletInfo3')}</li>
                  <li>• {t('warehouse.modules.picking.pallets.palletInfo4')}</li>
                </ul>
              </div>
            </div>
            <button
              onClick={handleDismissFirstTimeInfo}
              className="w-full text-sm font-medium text-[#0E7C86] hover:text-[#0A5F66] transition-colors min-h-[44px]"
            >
              {t('warehouse.modules.picking.pallets.understood')}
            </button>
          </div>
        )}

        {/* Warning for many empty pallets */}
        {emptyPallets.length > 3 && (
          <div
            className="mb-4 p-3 rounded-lg border-2 border-amber-400 bg-amber-50"
            role="alert"
            aria-live="polite"
          >
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">
                {t('warehouse.modules.picking.pallets.manyEmptyWarning', { 0: emptyPallets.length })}
              </p>
            </div>
            <button
              onClick={handleDeleteAllEmpty}
              className="w-full mt-2 text-sm font-medium text-amber-700 hover:text-amber-900 py-2 px-3 rounded border border-amber-400 hover:bg-amber-100 transition-colors min-h-[44px]"
              aria-label={t('warehouse.modules.picking.palletManager.deleteAllEmptyAria', { 0: emptyPallets.length })}
            >
              <Trash2 size={14} className="inline mr-1.5" />
              {t('warehouse.modules.picking.pallets.deleteAllEmpty')}
            </button>
          </div>
        )}

        <div className="mb-4 sm:mb-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium mb-3">{t('warehouse.modules.picking.pallets.newCarrier', {}, 'Ny lastbærer')}</h4>
          {/* Lastbærer-valg (36334224): settes på nye paller; default fra TransitPackaging.DefaultYesNo */}
          {carriers.length > 0 && onSelectCarrier && (
            <div className="mb-3">
              <label htmlFor="pallet-carrier-select" className="block text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.modules.picking.pallets.carrier', {}, 'Lastbærer')}
              </label>
              <select
                id="pallet-carrier-select"
                className="w-full p-3 sm:p-2 border border-gray-300 rounded min-h-[44px] bg-white"
                value={selectedCarrierId ?? ''}
                onChange={(e) => onSelectCarrier(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">{t('warehouse.modules.picking.pallets.noCarrier', {}, '(Ingen lastbærer)')}</option>
                {carriers.map((c) => (
                  <option key={c.transitPackagingID} value={c.transitPackagingID}>
                    {c.transitPackagingDescr}
                  </option>
                ))}
              </select>
            </div>
          )}
          <ScanTextInput
            placeholder={t('warehouse.modules.picking.pallets.ssccCode')}
            className="w-full p-3 sm:p-2 border border-gray-300 rounded mb-3 min-h-[44px]"
            value={newPalletSSCC}
            onChange={(e) => setNewPalletSSCC(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCreatePallet()}
            aria-label={t('warehouse.modules.picking.palletManager.ssccForNewPalletAria')}
          />
          <button
            onClick={handleCreatePallet}
            className="w-full bg-[#0E7C86] text-white py-3 sm:py-2 rounded hover:bg-[#0A5F66] transition-colors font-medium min-h-[44px]"
            aria-label={t('warehouse.modules.picking.pallets.createCarrierAria', {}, 'Opprett lastbærer')}
          >
            <Plus size={16} className="inline mr-2" />
            {t('warehouse.modules.picking.pallets.create')}
          </button>
          {/* Fordel uplasserte varer under plukk (TW 36396316). Alltid synlig når noe er plukket
              (TW 36397706) — Haucon vil kunne opprette lastbæreren fra fordelingsmodalen. */}
          {onDistribute && hasPickedLines && (
            <button
              onClick={onDistribute}
              className="w-full mt-2 border-2 border-[#0E7C86] text-[#0E7C86] py-3 sm:py-2 rounded hover:bg-[#E0F2F4] transition-colors font-medium min-h-[44px]"
            >
              <Split size={16} className="inline mr-2" />
              {t('warehouse.modules.picking.pallets.distributeUnplaced', { '0': String(unplacedQty) }, `Fordel uplasserte (${unplacedQty})`)}
            </button>
          )}
        </div>

        {ssccReports.length > 1 && onSelectSsccReport && (
          <div className="mb-4 sm:mb-6 p-4 bg-gray-50 rounded-lg">
            <label htmlFor="sscc-report-select" className="block font-medium mb-2">
              {t('warehouse.modules.picking.pallets.labelReport', {}, 'Etikett-mal')}
            </label>
            <select
              id="sscc-report-select"
              className="w-full p-3 sm:p-2 border border-gray-300 rounded min-h-[44px] bg-white"
              value={selectedSsccReportId ?? ''}
              onChange={(e) => onSelectSsccReport(e.target.value ? Number(e.target.value) : undefined)}
              aria-label={t('warehouse.modules.picking.pallets.labelReport', {}, 'Etikett-mal')}
            >
              {ssccReports.map((r) => (
                <option key={r.reportID} value={r.reportID}>
                  {r.description} (ID {r.reportID})
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="space-y-4">
          {pallets.map((pallet) => {
            const isActive = pallet.sscc === activePalletSSCC;
            const isEmpty = pallet.totalItems === 0;

            return (
              <div
                key={pallet.sscc}
                role="article"
                aria-label={`${carrierName(pallet.sscc)} ${pallet.sscc}, ${isEmpty ? t('warehouse.modules.picking.palletManager.emptyLabel') : t('warehouse.modules.picking.palletManager.itemsLabel', { 0: pallet.totalItems })}${isActive ? `, ${t('warehouse.modules.picking.palletManager.activeLabel')}` : ''}`}
                className={`rounded-lg p-4 transition-all ${
                  isEmpty
                    ? 'bg-gray-50 border-2 border-dashed border-gray-300'
                    : isActive
                    ? 'bg-white border-2 border-solid border-[#F58220] shadow-lg shadow-orange-100'
                    : 'bg-white border-2 border-solid border-[#0E7C86]'
                }`}
              >
                <div className="flex flex-col mb-3 gap-1">
                  {/* Navn, status og SSCC får full bredde — ikonene ligger i egen rad under (TW 36396318) */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h5 className="font-semibold text-gray-900">{carrierName(pallet.sscc)}</h5>
                      {isEmpty && (
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          aria-label={t('warehouse.modules.picking.palletManager.emptyPalletInfoAria')}
                          title={t('warehouse.modules.picking.pallets.notStoredInfo')}
                        >
                          <Info size={14} />
                        </button>
                      )}
                    </div>
                    {getPalletStatusBadge(pallet, isActive)}
                    <p className="text-xs text-gray-500 mt-1 break-all">{pallet.sscc}</p>
                    {pallet.ssccParent && (
                      <p className="text-xs text-gray-500">{t('warehouse.modules.picking.palletManager.parent')}: {pallet.ssccParent}</p>
                    )}
                  </div>
                  <div className="flex gap-1 flex-wrap items-center -ml-2">
                    {pallet.sscc !== activePalletSSCC && (
                      <button
                        onClick={() => onSetActivePallet(pallet.sscc)}
                        className="text-[#0E7C86] hover:text-[#0A5F66] transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                        aria-label={t('warehouse.modules.picking.pallets.activateTitle', { 0: pallet.sscc })}
                        title={t('warehouse.modules.picking.pallets.activate')}
                      >
                        <Check size={20} />
                      </button>
                    )}
                    {onChangeCarrier && carriers.length > 0 && (
                      <button
                        onClick={() => setCarrierPickerSscc(carrierPickerSscc === pallet.sscc ? null : pallet.sscc)}
                        className={`transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center ${
                          carrierPickerSscc === pallet.sscc ? 'text-[#F58220]' : 'text-gray-600 hover:text-gray-800'
                        }`}
                        aria-label={t('warehouse.modules.picking.pallets.changeCarrierAria', { 0: carrierName(pallet.sscc) }, `Bytt type for ${carrierName(pallet.sscc)}`)}
                        aria-expanded={carrierPickerSscc === pallet.sscc}
                      >
                        <ArrowLeftRight size={20} />
                      </button>
                    )}
                    {!isEmpty && pListId != null && (
                      <button
                        onClick={() => setEnrichSscc(pallet.sscc)}
                        className="text-gray-600 hover:text-gray-800 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                        aria-label={t('warehouse.modules.picking.enrichSscc.editTitle', { 0: pallet.sscc }, 'Rediger pall {0}')}
                        title={t('warehouse.modules.picking.enrichSscc.edit', {}, 'Rediger pall')}
                      >
                        <Pencil size={20} />
                      </button>
                    )}
                    <button
                      onClick={() => handlePrintSSCC(pallet)}
                      disabled={printingSSCC === pallet.sscc}
                      className="text-blue-600 hover:text-blue-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center disabled:opacity-50"
                      aria-label={t('warehouse.modules.picking.pallets.printTitle', { 0: pallet.sscc })}
                      title={t('warehouse.modules.picking.pallets.print')}
                    >
                      {printingSSCC === pallet.sscc ? (
                        <Loader2 size={20} className="animate-spin" />
                      ) : (
                        <Printer size={20} />
                      )}
                    </button>
                    {/* Innholdsetikett (36334106): etikett med pallens varelinjer */}
                    {!isEmpty && (
                      <button
                        onClick={() => handlePrintSSCC(pallet, true)}
                        disabled={printingSSCC === pallet.sscc}
                        className="text-teal-600 hover:text-teal-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center disabled:opacity-50"
                        aria-label={t('warehouse.modules.picking.pallets.printContentTitle', { 0: pallet.sscc }, `Skriv ut innholdsetikett for pall ${pallet.sscc}`)}
                        title={t('warehouse.modules.picking.pallets.printContent', {}, 'Innholdsetikett')}
                      >
                        <ClipboardList size={20} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDeletePallet(pallet)}
                      className="ml-auto text-red-600 hover:text-red-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                      aria-label={t('warehouse.modules.picking.pallets.deleteTitle', { 0: pallet.sscc })}
                      title={isEmpty ? t('warehouse.modules.picking.pallets.deleteEmptyTitle') : t('warehouse.modules.picking.pallets.deleteFilledTitle')}
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>

                {carrierPickerSscc === pallet.sscc && onChangeCarrier && (
                  <div className="mb-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      {t('warehouse.modules.picking.pallets.changeCarrierTitle', {}, 'Velg ny lastbærertype')}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {carriers.map((c) => {
                        const current = c.transitPackagingID === pallet.transitPackagingID;
                        return (
                          <button
                            key={c.transitPackagingID}
                            disabled={current}
                            onClick={() => {
                              onChangeCarrier(pallet.sscc, c.transitPackagingID);
                              setCarrierPickerSscc(null);
                            }}
                            className={`min-h-[44px] px-3 rounded border text-sm font-medium transition-colors ${
                              current
                                ? 'border-[#0E7C86] bg-[#E0F2F4] text-[#0E7C86]'
                                : 'border-gray-300 bg-white text-gray-800 hover:border-[#0E7C86]'
                            }`}
                          >
                            {c.transitPackagingDescr}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!isEmpty && (
                  <>
                    <div className="text-sm text-gray-700 mb-3 font-medium">
                      {pallet.totalItems} {pallet.totalItems === 1 ? t('warehouse.modules.picking.pallets.item') : t('warehouse.modules.picking.pallets.items')}, {pallet.totalQuantity} {t('warehouse.modules.picking.pallets.pieces')}, {pallet.totalWeight}{t('warehouse.modules.picking.pallets.weight')}
                    </div>

                    <div className="space-y-1">
                      {pallet.items.map((item) => (
                        <div key={`${item.orderNo}-${item.lineNo}`} className="text-xs text-gray-600 py-1 px-2 bg-gray-50 rounded">
                          {t('warehouse.modules.picking.palletManager.order')} {item.orderNo}, {t('warehouse.modules.picking.palletManager.line')} {item.lineNo}: <strong>{item.quantity} {t('warehouse.modules.picking.pallets.pieces')}</strong>
                          {item.gtin && <span className="text-gray-500"> (GTIN: {item.gtin})</span>}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {isEmpty && (
                  <p className="text-sm text-gray-500 italic mt-2">
                    {t('warehouse.modules.picking.pallets.emptyPalletInfo')}
                  </p>
                )}
              </div>
            );
          })}

          {pallets.length === 0 && (
            <div className="text-center text-gray-500 py-12">
              <Package2 size={48} className="mx-auto mb-3 text-gray-300" />
              <p className="font-medium text-gray-600">{t('warehouse.modules.picking.pallets.noPalletsYet')}</p>
              <p className="text-sm mt-1">{t('warehouse.modules.picking.pallets.createToStart')}</p>
            </div>
          )}
        </div>
      </div>

      {enrichSscc && pListId != null && (
        <EnrichSSCCModal
          pListId={pListId}
          sscc={enrichSscc}
          onClose={() => setEnrichSscc(null)}
          onSaved={() => onReload?.()}
        />
      )}
    </div>
  );
};
